import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminAiDatasetDetail,
  type AdminAiSplit,
} from "api/adminApi";
import {
  Badge,
  Button,
  ErrorBanner,
  Field,
  InfoRow,
  Input,
  Modal,
  NUMERIC,
  Select,
  Textarea,
  Toggle,
} from "pages/admin/ui";
import {
  ACTION_LABEL,
  FEATURES,
  LABEL_HELP,
  LABEL_TEXT,
  LABEL_TONE,
  SPLITS,
  answerOf,
  dpoPairs,
  editRatioLabel,
  fieldLabel,
  formatOccurredAt,
  commentMeta,
  inputLineItems,
  outputFieldsOf,
  parseTrainingConfig,
  serializeTrainingConfig,
  sftSample,
  toAdminOutput,
  type TrainingConfig,
} from "./aiFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

const errorMessageOf = (e: unknown) =>
  (e as AxiosError).response?.status === 403
    ? "권한이 없는 계정이에요"
    : FALLBACK_ERROR_MESSAGE;

type TrainingTab = "sft" | "dpo" | "rl";

const TRAINING_TABS: { key: TrainingTab; label: string }[] = [
  { key: "sft", label: "SFT" },
  { key: "dpo", label: "선호도 DPO" },
  { key: "rl", label: "강화학습 RL" },
];

// 모달 폼 상태 — 저장 단위와 같다 (서버 dataset/update 는 폼 전체를 받는다)
type FormState = {
  adminDraft: Record<string, string>;
  sftEnabled: boolean;
  dpoEnabled: boolean;
  rlEnabled: boolean;
  split: AdminAiSplit;
  config: TrainingConfig;
  memo: string;
};

const formOf = (detail: AdminAiDatasetDetail): FormState => ({
  adminDraft: Object.fromEntries(
    Object.entries(detail.adminOutput ?? {}).map(([k, v]) => [k, v ?? ""])
  ),
  sftEnabled: detail.sftEnabled,
  dpoEnabled: detail.dpoEnabled,
  rlEnabled: detail.rlEnabled,
  split: detail.split,
  config: parseTrainingConfig(detail.feature, detail.trainingConfig),
  memo: detail.memo ?? "",
});

/**
 * 데이터셋 행 상세 — 왼쪽은 이 행이 무엇인지(입력 스냅샷·AI·코치·관리자 보완), 오른쪽은 어디에 어떻게 쓸지(SFT·DPO·RL).
 * 보완 칸을 비우면 코치 값이 학습 정답이다. 적재된 원본 값은 바꾸지 않는다.
 */
export default function DatasetRowModal({
  id,
  rowIds,
  onNavigate,
  onSaved,
  onClose,
}: {
  id: number;
  // 목록의 현재 페이지 행 순서 — 이전/다음 이동용
  rowIds: number[];
  onNavigate: (id: number) => void;
  onSaved: (detail: AdminAiDatasetDetail) => void;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<AdminAiDatasetDetail | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [initialForm, setInitialForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<TrainingTab>("sft");

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    (async () => {
      try {
        const res = await adminApi.aiDatasetDetail(id);
        if (cancelled) return;
        if (res.code === 0 && res.result) {
          setDetail(res.result);
          setForm(formOf(res.result));
          setInitialForm(formOf(res.result));
        } else {
          setError(res.message || FALLBACK_ERROR_MESSAGE);
        }
      } catch (e) {
        if (cancelled) return;
        setError(errorMessageOf(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // 보완 칸은 비운 칸을 빼고 비교한다 — 입력했다 지운 칸({k: ""})을 변경으로 보지 않게
  const normalized = (f: FormState | null) =>
    f &&
    JSON.stringify({
      ...f,
      adminDraft: toAdminOutput(f.adminDraft),
      memo: f.memo.trim(),
    });
  const dirty = normalized(form) !== normalized(initialForm);

  // 저장하지 않은 변경이 있으면 떠나기 전에 묻는다.
  // 저장 중에는 떠나지 않는다 — 응답이 다른 행을 연 뒤에 도착하면 그 행 화면에 이 행 값이 덮인다
  const leave = useCallback(
    (next: () => void) => {
      if (saving) return;
      if (dirty && !window.confirm("저장하지 않은 변경이 있어요. 나갈까요?"))
        return;
      next();
    },
    [dirty, saving]
  );
  const close = useCallback(() => leave(onClose), [leave, onClose]);

  const index = rowIds.indexOf(id);
  const prevId = index > 0 ? rowIds[index - 1] : undefined;
  const nextId =
    index >= 0 && index < rowIds.length - 1 ? rowIds[index + 1] : undefined;

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  const updateConfig = (next: Partial<TrainingConfig>) =>
    setForm((prev) =>
      prev ? { ...prev, config: { ...prev.config, ...next } } : prev
    );

  const save = async () => {
    if (!detail || !form) return;
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.aiDatasetUpdate({
        id: detail.id,
        adminOutput: toAdminOutput(form.adminDraft),
        sftEnabled: form.sftEnabled,
        dpoEnabled: form.dpoEnabled,
        rlEnabled: form.rlEnabled,
        split: form.split,
        trainingConfig: serializeTrainingConfig(form.config),
        memo: form.memo.trim() || null,
      });
      if (res.code === 0 && res.result) {
        setDetail(res.result);
        setForm(formOf(res.result));
        setInitialForm(formOf(res.result));
        onSaved(res.result);
      } else {
        setError(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setError(errorMessageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const adminOutput = form ? toAdminOutput(form.adminDraft) : null;
  const sample = useMemo(
    () =>
      detail && form
        ? sftSample(detail, adminOutput, form.config.sft.systemPromptVersion)
        : null,
    // adminOutput 은 form 에서 파생된다
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail, form]
  );
  const pairs = useMemo(
    () =>
      detail && form ? dpoPairs(detail, adminOutput, form.config.dpo) : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail, form]
  );

  const featureLabel =
    FEATURES.find((f) => f.key === detail?.feature)?.label ?? "";

  return (
    <Modal
      title={`행 #${id}`}
      subject={
        detail
          ? `${featureLabel} · ${ACTION_LABEL[detail.action]}${
              detail.field ? ` · ${fieldLabel(detail.field)}` : ""
            } · ${detail.coachName ?? "코치 미상"} · 내담자 #${detail.patientId} · ${formatOccurredAt(
              detail.occurredAt
            )}`
          : undefined
      }
      // 좌우 입력·학습 설정을 나란히 보는 작업 화면이라 거의 전체 화면으로 연다 (오버레이 여백은 남긴다)
      width="96vw"
      height="96vh"
      onClose={close}
      footer={
        <>
          <span className="mr-auto text-xs text-slate-500">
            {detail?.adminUpdatedBy
              ? `마지막 수정 ${detail.adminUpdatedBy} · ${formatOccurredAt(detail.adminUpdatedAt ?? "")}`
              : "관리자 수정 없음"}
          </span>
          <Button
            disabled={prevId === undefined || saving}
            onClick={() =>
              prevId !== undefined && leave(() => onNavigate(prevId))
            }
          >
            이전 행
          </Button>
          <Button
            disabled={nextId === undefined || saving}
            onClick={() =>
              nextId !== undefined && leave(() => onNavigate(nextId))
            }
          >
            다음 행
          </Button>
          <Button
            variant="primary"
            disabled={!dirty || !form}
            loading={saving}
            loadingLabel="저장 중"
            onClick={save}
          >
            저장
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3">
          <ErrorBanner>{error}</ErrorBanner>
        </div>
      )}

      {!detail || !form ? (
        !error && (
          <div className="flex flex-col gap-2 py-6">
            <span className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
            <span className="h-40 w-full animate-pulse rounded bg-slate-100" />
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          {/* 왼쪽 — 이 행이 무엇인지 */}
          <div className="flex min-w-0 flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <span title={LABEL_HELP} className="cursor-help">
                <Badge tone={LABEL_TONE[detail.label]}>
                  라벨 · {LABEL_TEXT[detail.label]}
                </Badge>
              </span>
              {detail.editRatio != null && detail.label === "edited" && (
                <span className={`text-xs text-slate-500 ${NUMERIC}`}>
                  {editRatioLabel(detail.editRatio)}
                </span>
              )}
              <Badge tone="idle">분할 {form.split}</Badge>
              {detail.origin === "backfill" && (
                <Badge tone="warn">백필 · 입력 스냅샷 추정</Badge>
              )}
            </div>

            <Block title="입력" note="적재 시점 스냅샷">
              <InputSnapshot detail={detail} />
            </Block>

            <Block
              title="출력 · 정답 만들기"
              note="보완 칸을 비우면 코치 값이 정답"
            >
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full table-fixed border-collapse text-sm">
                  {/* 항목 이름만 고정 폭, 나머지 셋(AI·코치·보완)은 같은 폭 — 보완 칸이 좁으면 고쳐 쓰기 어렵다 */}
                  <colgroup>
                    <col className="w-28" />
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                      <th className="px-3 py-2 font-semibold">항목</th>
                      <th className="px-3 py-2 font-semibold">AI 초안</th>
                      <th className="px-3 py-2 font-semibold">코치</th>
                      <th className="px-3 py-2 font-semibold">관리자 보완</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outputFieldsOf(detail.feature).map(({ key, label }) => {
                      const fieldLabelValue = detail.fieldLabels?.[key];
                      return (
                        <tr
                          key={key}
                          className="border-b border-slate-100 align-top last:border-b-0"
                        >
                          <td className="px-3 py-2">
                            <p className="text-slate-700">{label}</p>
                            {fieldLabelValue && (
                              <span className="mt-1 inline-block">
                                <Badge tone={LABEL_TONE[fieldLabelValue]}>
                                  {LABEL_TEXT[fieldLabelValue]}
                                </Badge>
                              </span>
                            )}
                          </td>
                          <td className="whitespace-pre-wrap px-3 py-2 text-slate-500">
                            {detail.aiOutput?.[key] || (
                              <span className="text-slate-300">–</span>
                            )}
                          </td>
                          <td className="whitespace-pre-wrap px-3 py-2 text-slate-900">
                            {detail.coachOutput == null ? (
                              <span className="text-slate-300">(안 씀)</span>
                            ) : (
                              detail.coachOutput[key] || (
                                <span className="text-slate-300">–</span>
                              )
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Textarea
                              rows={2}
                              aria-label={`${label} 관리자 보완`}
                              placeholder={
                                answerOf(detail.coachOutput, null)[key] ?? ""
                              }
                              value={form.adminDraft[key] ?? ""}
                              onChange={(e) =>
                                update("adminDraft", {
                                  ...form.adminDraft,
                                  [key]: e.target.value,
                                })
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Block>

            {detail.groupRows.length > 1 && (
              <Block title="같은 원본의 행" note={detail.groupKey ?? ""}>
                <div className="flex flex-wrap gap-1.5">
                  {detail.groupRows.map((g) => (
                    <button
                      type="button"
                      key={g.id}
                      disabled={g.id === detail.id || saving}
                      onClick={() => leave(() => onNavigate(g.id))}
                      className={`rounded-md border px-2 py-1 text-xs ${
                        g.id === detail.id
                          ? "border-[#0F55A7]/30 bg-[#DCEFFA] text-[#0F55A7]"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      #{g.id} {ACTION_LABEL[g.action]}
                      {g.field ? ` · ${fieldLabel(g.field)}` : ""} ·{" "}
                      {LABEL_TEXT[g.label]}
                    </button>
                  ))}
                </div>
              </Block>
            )}

            <Field label="메모">
              <Textarea
                rows={2}
                maxLength={1000}
                value={form.memo}
                onChange={(e) => update("memo", e.target.value)}
              />
            </Field>
          </div>

          {/* 오른쪽 — 어디에 어떻게 쓸지 */}
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex gap-1 border-b border-slate-200">
              {TRAINING_TABS.map(({ key, label }) => {
                const on =
                  key === "sft"
                    ? form.sftEnabled
                    : key === "dpo"
                      ? form.dpoEnabled
                      : form.rlEnabled;
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setTab(key)}
                    className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${
                      tab === key
                        ? "border-[#0F55A7] font-semibold text-[#0F55A7]"
                        : "border-transparent text-slate-500 hover:text-slate-900"
                    }`}
                  >
                    {label}
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${on ? "bg-emerald-500" : "bg-slate-300"}`}
                      aria-label={on ? "사용" : "사용 안 함"}
                    />
                  </button>
                );
              })}
            </div>

            {tab === "sft" && (
              <div className="flex flex-col gap-4">
                <Toggle
                  checked={form.sftEnabled}
                  onChange={(v) => update("sftEnabled", v)}
                  label="이 행을 SFT 학습 데이터로 사용"
                  helper="입력 → 정답을 그대로 따라 배우는 학습"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="분할"
                    helper="내담자 단위 자동 배정. 바꾸면 이 행만 바뀐다"
                  >
                    <Select
                      value={form.split}
                      onChange={(e) =>
                        update("split", e.target.value as AdminAiSplit)
                      }
                    >
                      {SPLITS.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="system 프롬프트 버전">
                    <Input
                      value={form.config.sft.systemPromptVersion}
                      onChange={(e) =>
                        updateConfig({
                          sft: { systemPromptVersion: e.target.value },
                        })
                      }
                    />
                  </Field>
                </div>
                <Preview title="만들어지는 샘플 (messages)">
                  {sample
                    ? JSON.stringify(sample, null, 2)
                    : "정답이 비어 있어 샘플이 만들어지지 않아요 (안 씀 행은 관리자 보완으로 정답을 채우면 쓸 수 있어요)"}
                </Preview>
              </div>
            )}

            {tab === "dpo" && (
              <div className="flex flex-col gap-4">
                <Toggle
                  checked={form.dpoEnabled}
                  onChange={(v) => update("dpoEnabled", v)}
                  label="이 행을 선호도 학습(DPO) 쌍으로 사용"
                  helper="같은 입력에서 더 나은 답(chosen)과 덜 나은 답(rejected)을 대비해 배우는 학습"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="chosen (선택된 답)">
                    <Input value="정답 (코치 + 관리자 보완)" disabled />
                  </Field>
                  <Field label="rejected (거부된 답)">
                    <Select
                      value={form.config.dpo.rejected}
                      onChange={(e) =>
                        updateConfig({
                          dpo: {
                            ...form.config.dpo,
                            rejected: e.target.value as "ai" | "custom",
                          },
                        })
                      }
                    >
                      <option value="ai">AI 초안</option>
                      <option value="custom">직접 입력</option>
                    </Select>
                  </Field>
                </div>
                {form.config.dpo.rejected === "custom" && (
                  <Field label="rejected 직접 입력">
                    <Textarea
                      rows={3}
                      value={form.config.dpo.rejectedCustom}
                      onChange={(e) =>
                        updateConfig({
                          dpo: {
                            ...form.config.dpo,
                            rejectedCustom: e.target.value,
                          },
                        })
                      }
                    />
                  </Field>
                )}
                {detail.feature === "automatic_thought" &&
                  form.config.dpo.rejected === "ai" && (
                    <Field label="쌍 단위">
                      <Select
                        value={form.config.dpo.pairUnit}
                        onChange={(e) =>
                          updateConfig({
                            dpo: {
                              ...form.config.dpo,
                              pairUnit: e.target.value as "whole" | "field",
                            },
                          })
                        }
                      >
                        <option value="whole">전체 한 쌍</option>
                        <option value="field">바뀐 항목마다 한 쌍</option>
                      </Select>
                    </Field>
                  )}
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-medium text-slate-600">
                    만들어지는 쌍 {pairs.length}개
                  </p>
                  {pairs.length === 0 ? (
                    <p className="text-sm text-slate-400">
                      정답과 rejected 가 같거나 비어 있어 쌍이 없어요
                    </p>
                  ) : (
                    pairs.map((p) => (
                      <div
                        key={p.title}
                        className="rounded-lg border border-slate-200 p-3 text-sm"
                      >
                        <p className="text-xs font-semibold text-slate-500">
                          {p.title}
                        </p>
                        <p className="mt-1.5 whitespace-pre-wrap">
                          <span className="mr-1.5 text-xs font-medium text-emerald-700">
                            chosen
                          </span>
                          {p.chosen}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-slate-500">
                          <span className="mr-1.5 text-xs font-medium text-rose-600">
                            rejected
                          </span>
                          {p.rejected || "(비어 있음)"}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {tab === "rl" && (
              <div className="flex flex-col gap-4">
                <Toggle
                  checked={form.rlEnabled}
                  onChange={(v) => update("rlEnabled", v)}
                  label="이 행을 강화학습(RL)에 사용"
                  helper="모델이 답을 만들고, 보상 함수 점수로 스스로 나아지는 학습. 정답은 reference 로만 쓴다"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="쓰임">
                    <Select
                      value={form.config.rl.usage}
                      onChange={(e) =>
                        updateConfig({
                          rl: {
                            ...form.config.rl,
                            usage: e.target.value as "train" | "eval",
                          },
                        })
                      }
                    >
                      <option value="train">학습 프롬프트 풀</option>
                      <option value="eval">평가셋 (학습에 안 씀)</option>
                    </Select>
                  </Field>
                  <Field label="채점 방식">
                    <Select
                      value={form.config.rl.grading}
                      onChange={(e) =>
                        updateConfig({
                          rl: {
                            ...form.config.rl,
                            grading: e.target
                              .value as TrainingConfig["rl"]["grading"],
                          },
                        })
                      }
                    >
                      <option value="mixed">규칙 + LLM 심사 혼합</option>
                      <option value="rule">규칙만</option>
                      <option value="llm">LLM 심사만</option>
                    </Select>
                  </Field>
                </div>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                        <th className="w-12 px-3 py-2 font-semibold">사용</th>
                        <th className="px-3 py-2 font-semibold">
                          보상 기준 (reward)
                        </th>
                        <th className="w-20 px-3 py-2 text-right font-semibold">
                          가중치
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.config.rl.rewards.map((r, i) => {
                        const setReward = (next: Partial<typeof r>) =>
                          updateConfig({
                            rl: {
                              ...form.config.rl,
                              rewards: form.config.rl.rewards.map((x, j) =>
                                j === i ? { ...x, ...next } : x
                              ),
                            },
                          });
                        return (
                          <tr
                            key={r.key}
                            className="border-b border-slate-100 align-top last:border-b-0"
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                aria-label={`${r.label} 사용`}
                                checked={r.on}
                                onChange={(e) =>
                                  setReward({ on: e.target.checked })
                                }
                              />
                            </td>
                            <td className="px-3 py-2">
                              <p className="text-slate-900">{r.label}</p>
                              <p className="text-xs text-slate-500">{r.how}</p>
                            </td>
                            <td className="px-3 py-2">
                              <Input
                                type="number"
                                sizing="sm"
                                min={0}
                                step={0.5}
                                aria-label={`${r.label} 가중치`}
                                className={`text-right ${NUMERIC}`}
                                value={r.weight}
                                disabled={!r.on}
                                onChange={(e) =>
                                  setReward({ weight: Number(e.target.value) })
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Field label="LLM 심사 루브릭 메모">
                  <Textarea
                    rows={3}
                    value={form.config.rl.rubric}
                    onChange={(e) =>
                      updateConfig({
                        rl: { ...form.config.rl, rubric: e.target.value },
                      })
                    }
                  />
                </Field>
                <Preview title="reference (정답)">
                  {JSON.stringify(
                    answerOf(detail.coachOutput, adminOutput),
                    null,
                    2
                  )}
                </Preview>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Block({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        {note && <p className="truncate text-xs text-slate-400">{note}</p>}
      </div>
      {children}
    </div>
  );
}

function Preview({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-medium text-slate-600">{title}</p>
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">
        {children}
      </pre>
    </div>
  );
}

// 입력 스냅샷 — 자동적 사고는 대화 줄, 채팅은 내담자 메시지, 코멘트는 위치 문장 + 대화(위치 줄 강조) + 기록지 원문 JSON(접어 둠)
function InputSnapshot({ detail }: { detail: AdminAiDatasetDetail }) {
  const lines = inputLineItems(detail.feature, detail.input);
  const raw = detail.input?.thought_record_data;
  const meta =
    detail.feature === "thought_record_comment"
      ? commentMeta(detail.input)
      : null;
  return (
    <div className="flex flex-col gap-2">
      {meta && (
        <div className="flex flex-col gap-1 text-sm">
          <InfoRow label="코멘트 위치">
            {meta.locationText ?? <span className="text-slate-400">–</span>}
          </InfoRow>
          {meta.commentType && (
            <InfoRow label="코멘트 유형">{meta.commentType}</InfoRow>
          )}
        </div>
      )}
      {lines.length === 0 ? (
        <p className="text-sm text-slate-400">입력 스냅샷이 없어요</p>
      ) : (
        <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
          {lines.map((line, i) => (
            <p
              key={i}
              className={`whitespace-pre-wrap ${
                line.isTarget
                  ? "-mx-1 rounded bg-amber-100 px-1 font-medium text-slate-900"
                  : ""
              }`}
            >
              {line.text}
              {line.isTarget && (
                <span className="ml-1 text-xs text-amber-700">
                  ← 코멘트 위치
                </span>
              )}
            </p>
          ))}
        </div>
      )}
      {typeof raw === "string" && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer">생각기록지 원문 JSON</summary>
          <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-slate-200 bg-slate-50 p-3">
            {raw}
          </pre>
        </details>
      )}
    </div>
  );
}
